import { FC, useCallback } from "react";
import { observer } from "mobx-react-lite";
import { StepRenderProps } from "@/components/onboarding/components/welcome-wizard/onboarding-steps.types";
import { useOnboardingSystemPermissionsHandlers } from "@/components/onboarding/components/welcome-wizard/shared";
import { OnboardingVoicePermissionsScreen } from "@/components/onboarding/components/welcome-wizard/steps/system-permission-voice/components";
import { OnboardingPermissionsRestartScreen } from "@/components/onboarding/components/welcome-wizard/steps/system-permission-voice/components";
import { TrackedEvent, trackEvent } from "@/domains/metrics";
import { useEffectOnMount } from "@/domains/react/useEffectOnMount";
import { useAppStore } from "@/store";

export const SystemPermissionsVoicePermissionsStep: FC<StepRenderProps> = observer(
  ({ onComplete }) => {
    const { store } = useAppStore();
    const showFinish = store.onboarding.showFinish;

    const {
      resolveConfigurationStatus,
      handleEnablePermission,
      handleOpenPermissionPane,
      restartRequired,
    } = useOnboardingSystemPermissionsHandlers();

    const handleDone = useCallback(() => {
      onComplete();
    }, [onComplete]);

    const handleNext = useCallback(() => {
      trackEvent(TrackedEvent.VoiceSetupSubmit, {
        enabled: true,
      });

      // restartRequired is React hook state — when true, the component
      // will re-render and show the restart screen below.
      if (!restartRequired) {
        handleDone();
      }
    }, [handleDone, restartRequired]);

    const handleSkip = () => {
      trackEvent(TrackedEvent.VoiceSetupSubmit, {
        enabled: false,
      });

      handleDone();
    };

    useEffectOnMount(() => {
      trackEvent(TrackedEvent.VoiceSetupView);
    });

    if (restartRequired) {
      return <OnboardingPermissionsRestartScreen onClose={handleDone} />;
    }

    return (
      <OnboardingVoicePermissionsScreen
        onNext={handleNext}
        onSkip={handleSkip}
        showFinish={showFinish}
        restartRequired={restartRequired}
        resolveConfigurationStatus={resolveConfigurationStatus}
        handleEnablePermission={handleEnablePermission}
        handleOpenPermissionPane={handleOpenPermissionPane}
      />
    );
  }
);
